/**
 * Authentication optimization utilities
 * Provides faster authentication through caching and preloading
 */

import { User } from '../types/auth';

export interface CachedUserData {
  user: User;
  timestamp: number;
}

export const AUTH_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
export const CACHE_KEYS = {
  USER: 'cached_user',
  TIMESTAMP: 'user_cache_timestamp',
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
} as const;

/**
 * Get cached user data if it's still valid
 */
export function getCachedUser(): User | null {
  try {
    const cachedUser = localStorage.getItem(CACHE_KEYS.USER);
    const cacheTimestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
    
    if (!cachedUser || !cacheTimestamp) return null;
    
    const now = Date.now();
    const cacheAge = now - parseInt(cacheTimestamp);
    
    if (cacheAge < AUTH_CACHE_DURATION) {
      return JSON.parse(cachedUser);
    }
    
    // Cache expired, clean it up
    clearUserCache();
    return null;
  } catch (error) {
    console.warn('Error reading cached user data:', error);
    clearUserCache();
    return null;
  }
}

/**
 * Cache user data with timestamp
 */
export function cacheUser(user: User): void {
  try {
    localStorage.setItem(CACHE_KEYS.USER, JSON.stringify(user));
    localStorage.setItem(CACHE_KEYS.TIMESTAMP, Date.now().toString());
  } catch (error) {
    console.warn('Error caching user data:', error);
  }
}

/**
 * Clear all cached user data
 */
export function clearUserCache(): void {
  localStorage.removeItem(CACHE_KEYS.USER);
  localStorage.removeItem(CACHE_KEYS.TIMESTAMP);
}

/**
 * Clear all authentication data
 */
export function clearAuthData(): void {
  localStorage.removeItem(CACHE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(CACHE_KEYS.REFRESH_TOKEN);
  clearUserCache();
}

/**
 * Check if user has valid authentication tokens
 */
export function hasValidTokens(): boolean {
  const accessToken = localStorage.getItem(CACHE_KEYS.ACCESS_TOKEN);
  return !!accessToken;
}

/**
 * Get cache age in milliseconds
 */
export function getCacheAge(): number {
  const cacheTimestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
  if (!cacheTimestamp) return Infinity;
  
  return Date.now() - parseInt(cacheTimestamp);
}

/**
 * Check if cache is still valid
 */
export function isCacheValid(): boolean {
  return getCacheAge() < AUTH_CACHE_DURATION;
}

/**
 * Preload authentication state synchronously
 * This runs before React renders to provide instant auth state
 */
export function preloadAuthState(): { user: User | null; hasToken: boolean } {
  const hasToken = hasValidTokens();
  const cachedUser = getCachedUser();
  
  return {
    user: cachedUser,
    hasToken,
  };
}